import type { DateTime } from "luxon";
import { useCallback } from "react";
import { type HospitalDataContextType, useHospitalData } from "../contexts/HospitalDataProvider";
import type { ScheduleSlot } from "../utils/departmentWeeks";

export type ScheduleSaves = Pick<
	HospitalDataContextType,
	| "addNurseToShift"
	| "acceptNurseChanges"
	| "removeNurseFromSpecificDate"
	| "removeNurseCompletelyFromShift"
	| "restoreNurseToSpecificDate"
	| "addNewDBNurseShifts"
	| "removeNursesShiftsCompletely"
	| "moveNurseToSpecificDate"
	| "updateNurseChargeStatus"
	| "updateNurseDayTrainingStatus"
	| "updateShiftBonus"
	| "updateNurseNotes"
	| "updateNurseLocationAndTimes"
	| "updateNurse"
	| "getShiftScheduleDates"
	| "afterScheduleWrite"
	| "afterPublish"
	| "refreshScheduleCache"
	| "writeScheduleDay"
> & {
	/**
	 * Where a date's shift sits in the old list, for what still reads the old list by position: the slot fields a
	 * publish change carries (the server requires them but never reads them), and the Week view's highlights.
	 */
	oldListSlotOf: (date: DateTime, shiftType: string) => ScheduleSlot | undefined;
};

/**
 * The schedule saves a panel makes after the server call: the one place a panel's saves go through. Every screen that
 * hasn't moved to the cache yet shows the old list, so a save keeps editing it in both flag states, and the provider
 * marks the saved date's cached week stale. When the old list goes, only this hook changes.
 */
export const useScheduleSaves = (): ScheduleSaves => {
	const {
		addNurseToShift,
		acceptNurseChanges,
		removeNurseFromSpecificDate,
		removeNurseCompletelyFromShift,
		restoreNurseToSpecificDate,
		addNewDBNurseShifts,
		removeNursesShiftsCompletely,
		moveNurseToSpecificDate,
		updateNurseChargeStatus,
		updateNurseDayTrainingStatus,
		updateShiftBonus,
		updateNurseNotes,
		updateNurseLocationAndTimes,
		updateNurse,
		getShiftScheduleDates,
		afterScheduleWrite,
		afterPublish,
		refreshScheduleCache,
		writeScheduleDay,
		getScheduleSlot,
	} = useHospitalData();
	const oldListSlotOf = useCallback((date: DateTime, shiftType: string) => getScheduleSlot(date, shiftType), [getScheduleSlot]);
	return {
		addNurseToShift,
		acceptNurseChanges,
		removeNurseFromSpecificDate,
		removeNurseCompletelyFromShift,
		restoreNurseToSpecificDate,
		addNewDBNurseShifts,
		removeNursesShiftsCompletely,
		moveNurseToSpecificDate,
		updateNurseChargeStatus,
		updateNurseDayTrainingStatus,
		updateShiftBonus,
		updateNurseNotes,
		updateNurseLocationAndTimes,
		updateNurse,
		getShiftScheduleDates,
		afterScheduleWrite,
		afterPublish,
		refreshScheduleCache,
		writeScheduleDay,
		oldListSlotOf,
	};
};
